// TutorialPublicPage - Premium public view of a published tutorial
// Features: Hero header, Cover image, Table of Contents, Interactive Checklist, Media Gallery, Navigation
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import LinkExtension from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Youtube from '@tiptap/extension-youtube';
import TextAlign from '@tiptap/extension-text-align';
import UnderlineExtension from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';
import { TextStyle, Color, BackgroundColor } from '@tiptap/extension-text-style';
import {
  User, Clock, BookOpen, ChevronLeft, ChevronRight,
  List, ImageIcon, Film, Loader2, AlertCircle, ArrowLeft,
  CheckSquare, FileText, X, Wrench, Sparkles, Lock, Edit3,
  Home, ArrowUp, ArrowDown, Layers,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { tutorialAPI } from '@/lib/api';
import { TutorialCodeBlock } from '@/components/tutorials/CodeBlockView';
import { HtmlBlock } from '@/components/tutorials/HtmlBlockView';
import ContentProtection from '@/components/tutorials/ContentProtection';
import { useAuth } from '@/lib/auth-context';
import { getCategoryTheme } from './TutorialsListPage';
import './tutorial-public.css';

// ==================== HELPERS ====================

// Extract headings from TipTap JSON for TOC (Only H1, H2, and main step titles)
function extractHeadings(content) {
  if (!content) return [];
  let parsed = content;
  if (typeof content === 'string') {
    try { parsed = JSON.parse(content); } catch { return []; }
  }
  if (!parsed || !parsed.content) return [];
  const headings = [];
  let counter = 0;
  const ignoreRegex = /^(travail attendu|contraintes|checklist|validation|objectif|prérequis|remarque|note)/i;

  const walk = (nodes) => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (node.type === 'heading' && node.attrs?.level) {
        const text = extractText(node).trim();
        if (text && node.attrs.level <= 2 && !ignoreRegex.test(text)) {
          counter++;
          headings.push({
            id: `heading-${counter}`,
            level: node.attrs.level,
            text: text,
          });
        }
      } else if (node.type === 'paragraph') {
        const text = extractText(node).trim();
        const isMainStep = /^Étape\s+\d+|^Step\s+\d+|^Partie\s+\d+|^Phase\s+\d+|^Consignes|^Livrables|^Grille/i.test(text);
        if (text && isMainStep) {
          counter++;
          headings.push({
            id: `heading-${counter}`,
            level: 2,
            text: text,
          });
        }
      }
      if (node.content) walk(node.content);
    }
  };

  walk(parsed.content);
  return headings;
}

// Extract plain text from a node
function extractText(node) {
  if (!node) return '';
  if (node.type === 'text') return node.text || '';
  if (node.content) return node.content.map(extractText).join('');
  return '';
}

// Extract media (images + videos) from TipTap JSON
function extractMedia(content) {
  if (!content || !content.content) return { images: [], videos: [] };
  const images = [];
  const videos = [];

  const walk = (nodes) => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (node.type === 'image' && node.attrs?.src) {
        images.push({
          src: node.attrs.src,
          alt: node.attrs.alt || '',
          title: node.attrs.title || '',
        });
      }
      if (node.type === 'youtube' && node.attrs?.src) {
        videos.push({
          src: node.attrs.src,
          type: 'youtube',
          title: 'Vidéo YouTube',
        });
      }
      if (node.content) walk(node.content);
    }
  };

  walk(content.content);
  return { images, videos };
}

// Estimate reading time
function estimateReadingTime(wordCount) {
  const minutes = Math.ceil((wordCount || 0) / 200);
  return minutes < 1 ? '< 1 min' : `${minutes} min`;
}

// Format date
function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return dateStr.slice(0, 10);
  }
}

// ==================== CHECKLIST PERSISTENCE ====================

function getCheckedItems(pageId) {
  try {
    const data = localStorage.getItem(`tut-checklist-${pageId}`);
    return data ? JSON.parse(data) : {};
  } catch {
    return {};
  }
}

function setCheckedItems(pageId, items) {
  try {
    localStorage.setItem(`tut-checklist-${pageId}`, JSON.stringify(items));
  } catch { /* ignore */ }
}

// Réinjecte l'état sauvegardé dans le JSON du document (avant setContent)
// Plus fiable que patcher le DOM : ProseMirror reste la source de vérité
function applySavedChecks(content, saved) {
  if (!content || !saved || Object.keys(saved).length === 0) return content;
  let doc;
  try {
    doc = JSON.parse(JSON.stringify(typeof content === 'string' ? JSON.parse(content) : content));
  } catch {
    return content;
  }

  let idx = 0;
  const walk = (nodes) => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (node.type === 'taskItem') {
        const savedValue = saved[`task-${idx}`];
        if (savedValue !== undefined) {
          node.attrs = { ...(node.attrs || {}), checked: !!savedValue };
        }
        idx++;
      }
      if (node.content) walk(node.content);
    }
  };

  walk(doc.content);
  return doc;
}

// Position ProseMirror du n-ième taskItem (même ordre que le DOM : parcours préfixe)
function findTaskItemPos(editor, index) {
  let i = 0;
  let found = null;
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === 'taskItem') {
      if (i === index) found = pos;
      i += 1;
    }
    return true;
  });
  return found;
}

// ==================== COMPONENT ====================

export default function TutorialPublicPage() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const { user, isCandidate, canManage, isTrainer, isAdmin, isManager } = useAuth();

  // Admins et managers ont accès complet au tutoriel
  const isAdminOrManager = Boolean(
    user &&
    (
      user.role === 'admin' ||
      user.role === 'manager' ||
      isAdmin ||
      isManager
    )
  );

  // Seuls les formateurs (non admin/manager) voient la page "en cours de construction"
  const isFormateurOnly = Boolean(
    user &&
    !isAdminOrManager &&
    (
      user.role === 'trainer' ||
      user.role === 'formateur' ||
      (isTrainer && !isAdmin && !isManager)
    )
  );

  const protectionActive = !['admin', 'formateur', 'manager'].includes(user?.role) && !isAdmin && !isManager;
  const viewer = useMemo(() => (user
    ? {
      id: user.id,
      label: [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email,
    }
    : null), [user]);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tocOpen, setTocOpen] = useState(false);
  const [isReadingMode, setIsReadingMode] = useState(false);
  const articleRef = useRef(null);

  // Fetch public tutorial
  useEffect(() => {
    if (!pageId) return;
    setLoading(true);
    setError(null);

    tutorialAPI.getPublic(pageId)
      .then(data => {
        setPage(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message || 'Tutoriel non trouvé');
        setLoading(false);
      });
  }, [pageId]);

  // Extract TOC and media
  const headings = useMemo(() => page?.content ? extractHeadings(page.content) : [], [page?.content]);
  const media = useMemo(() => page?.content ? extractMedia(page.content) : { images: [], videos: [] }, [page?.content]);

  // Read-only editor with TaskList support
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, codeBlock: false }),
      TutorialCodeBlock,
      LinkExtension.configure({ openOnClick: true, HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' } }),
      Image.configure({ allowBase64: false, HTMLAttributes: { class: 'tut-editor-image' } }),
      Youtube.configure({ HTMLAttributes: { class: 'tut-editor-youtube' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      UnderlineExtension,
      Highlight.configure({ multicolor: true }),
      TextStyle,
      Color,
      BackgroundColor,
      HtmlBlock,
      TaskList,
      TaskItem.configure({ nested: true, onReadOnlyChecked: () => true }),
    ],
    content: page?.content || { type: 'doc', content: [{ type: 'paragraph' }] },
    editable: false,
  });

  // Update editor content when page changes
  useEffect(() => {
    if (editor && page?.content && !editor.isDestroyed) {
      const restored = pageId
        ? applySavedChecks(page.content, getCheckedItems(pageId))
        : page.content;
      editor.commands.setContent(restored, false);
    }
  }, [page?.content, editor, pageId]);

  // Inject heading IDs into DOM after editor renders
  useEffect(() => {
    if (!editor || !articleRef.current) return;

    const timer = setTimeout(() => {
      const container = articleRef.current;
      if (!container) return;
      const domHeadings = container.querySelectorAll('h1, h2, h3, h4, h5, h6, p');
      let counter = 0;
      const ignoreRegex = /^(travail attendu|contraintes|checklist|validation|objectif|prérequis|remarque|note)/i;

      domHeadings.forEach((el) => {
        const text = el.textContent?.trim();
        if (!text) return;
        if (el.tagName.startsWith('H')) {
          const level = parseInt(el.tagName.replace('H', ''), 10);
          if (level <= 2 && !ignoreRegex.test(text)) {
            counter++;
            el.id = `heading-${counter}`;
            el.style.scrollMarginTop = '85px';
          }
        } else if (el.tagName === 'P') {
          const isMainStep = /^Étape\s+\d+|^Step\s+\d+|^Partie\s+\d+|^Phase\s+\d+|^Consignes|^Livrables|^Grille/i.test(text);
          if (isMainStep) {
            counter++;
            el.id = `heading-${counter}`;
            el.style.scrollMarginTop = '85px';
          }
        }
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [editor, headings, page?.content]);

  // Checklist interactive pour les candidats
  useEffect(() => {
    if (!articleRef.current || !pageId || !editor) return;

    const container = articleRef.current;

    const handleClick = (e) => {
      if (e.target.closest('label') || e.target.closest('a')) return;
      const li = e.target.closest('ul[data-type="taskList"] > li');
      if (!li || !container.contains(li)) return;
      if (window.getSelection()?.toString()) return;

      const cb = li.querySelector('label input[type="checkbox"]');
      if (!cb) return;
      cb.click();
    };

    const handleChange = (e) => {
      const cb = e.target;
      if (!cb || cb.type !== 'checkbox') return;

      const li = cb.closest('ul[data-type="taskList"] > li');
      if (!li) return;

      const items = Array.from(container.querySelectorAll('ul[data-type="taskList"] > li'));
      const idx = items.indexOf(li);
      if (idx === -1) return;

      const checked = cb.checked;

      const pos = findTaskItemPos(editor, idx);
      if (pos !== null && !editor.isDestroyed) {
        const { state } = editor.view;
        const node = state.doc.nodeAt(pos);
        if (node) {
          editor.view.dispatch(state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, checked }));
        }
      }

      const currentSaved = getCheckedItems(pageId);
      currentSaved[`task-${idx}`] = checked;
      setCheckedItems(pageId, currentSaved);
    };

    container.addEventListener('change', handleChange);
    container.addEventListener('click', handleClick);

    return () => {
      container.removeEventListener('change', handleChange);
      container.removeEventListener('click', handleClick);
    };
  }, [editor, pageId, page?.content]);

  // Scroll to heading
  const scrollToHeading = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Navigation verticale rapide (flèches flottantes)
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const scrollToBottom = useCallback(() => {
    // scrollHeight plutôt qu'une valeur fixe : la page grandit avec les
    // images chargées en lazy et la galerie de médias.
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth',
    });
  }, []);

  const [activeHeadingId, setActiveHeadingId] = useState('');

  // ScrollSpy
  useEffect(() => {
    if (headings.length === 0) return;

    const handleScroll = () => {
      const headingElements = headings.map(h => document.getElementById(h.id)).filter(Boolean);
      const scrollPosition = window.scrollY + 130;

      let currentActiveId = headings[0]?.id || '';
      for (const el of headingElements) {
        if (el.offsetTop <= scrollPosition) {
          currentActiveId = el.id;
        } else {
          break;
        }
      }
      setActiveHeadingId(currentActiveId);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [headings]);

  // Loading state
  if (loading) {
    return (
      <div className="tut-public-page">
        <div className="tut-public-loading">
          <Loader2 className="animate-spin text-indigo-500" size={40} />
          <p className="text-muted-foreground">Chargement du tutoriel...</p>
        </div>
      </div>
    );
  }

  // Handler for return button to navigate back to the course page
  const handleReturnToCourses = () => {
    const wsId = page?.workspace_id || localStorage.getItem('current_workspace_id');
    if (wsId) {
      navigate(`/workspace/${wsId}/courses`);
    } else if (window.history.length > 1 && document.referrer) {
      navigate(-1);
    } else {
      navigate('/dashboard');
    }
  };

  // Seuls les formateurs (pas admin/manager) voient la page "Tutoriel en cours de construction"
  if (isFormateurOnly) {
    return (
      <div className="tut-public-page min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white relative overflow-hidden">
        {/* Decorative background glow */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-10 right-10 w-72 h-72 bg-purple-500/10 rounded-full blur-2xl pointer-events-none" />

        {/* Floating navbar header */}
        <div className="absolute top-0 left-0 right-0 p-6 flex items-center justify-between max-w-6xl mx-auto w-full z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-600/30 border border-indigo-500/30 backdrop-blur-md flex items-center justify-center text-indigo-400 font-bold">
              <BookOpen size={20} />
            </div>
            <div>
              <span className="text-xs uppercase tracking-wider text-indigo-300 font-bold">EFIlearn</span>
              <p className="text-sm font-semibold text-white/90 truncate max-w-xs sm:max-w-md">
                {page?.title || 'Tutoriel'}
              </p>
            </div>
          </div>
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/30 flex items-center gap-1.5 backdrop-blur-md">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            Mode Formateur
          </span>
        </div>

        {/* Main Glassmorphic Card */}
        <div className="max-w-xl w-full bg-slate-900/80 backdrop-blur-xl border border-indigo-500/20 rounded-3xl p-8 sm:p-12 shadow-2xl relative z-10 text-center space-y-6 mt-16 my-8 animate-in fade-in zoom-in-95 duration-300">
          {/* Construction Icon Badge */}
          <div className="relative inline-flex items-center justify-center mb-2">
            <div className="w-24 h-24 rounded-3xl bg-gradient-to-tr from-amber-500/20 to-indigo-500/20 border border-amber-500/30 flex items-center justify-center shadow-inner relative">
              <Wrench size={42} className="text-amber-400 animate-bounce" style={{ animationDuration: '3s' }} />
              <div className="absolute -top-2 -right-2 bg-indigo-600 p-2 rounded-xl text-white shadow-lg">
                <Sparkles size={16} />
              </div>
            </div>
          </div>

          {/* Titles & Message */}
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs font-medium">
              <span>🚧 Support Pédagogique</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight bg-gradient-to-r from-white via-slate-100 to-indigo-200 bg-clip-text text-transparent">
              Tutoriel en cours de construction
            </h1>
          </div>

          {/* Action Button */}
          <div className="flex justify-center pt-4 border-t border-slate-800/80">
            <button
              onClick={handleReturnToCourses}
              className="px-8 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold text-sm shadow-lg shadow-indigo-600/30 transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <ArrowLeft size={16} />
              Retour aux cours
            </button>
          </div>
        </div>

        {/* Footer info */}
        <p className="text-xs text-slate-500 z-10">
          EFIlearn • Espace Formateur & Administration Pédagogique
        </p>
      </div>
    );
  }

  // Error state
  if (error || !page) {
    return (
      <div className="tut-public-page">
        <div className="tut-public-not-found">
          <AlertCircle size={48} className="text-muted-foreground/40" />
          <h2 className="text-xl font-semibold">Tutoriel non disponible</h2>
          <p className="text-muted-foreground text-sm">
            {error || 'Ce tutoriel n\'est pas publié ou n\'existe pas.'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors text-sm font-medium"
          >
            <ArrowLeft size={16} />
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  // Page principale du tutoriel : fournie par l'API. Repli sur parent_id si le
  // backend n'expose pas encore root_page, pour que le bouton reste utile.
  const rootPage = page.root_page
    || (page.parent_id ? { id: page.parent_id, title: 'Page principale', icon: null } : null);

  const theme = getCategoryTheme(page?.category);

  // Vue 1 : PAGE DE GARDE SEULE (Ecran autonome axe sur l'image de couverture hero)
  if (!isReadingMode) {
    return (
      <div className="tut-public-page min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col justify-between p-3 sm:p-6">
        {/* En-tête marque */}
        <div className="max-w-4xl w-full mx-auto flex items-center justify-between py-1 mb-3">
          <Link to="/" className="flex items-center gap-2 text-indigo-600 font-bold text-sm">
            <BookOpen size={20} className="text-indigo-600" />
            <span>EFIlearn</span>
          </Link>
          <Badge variant="outline" className="text-[11px] font-semibold px-2.5 py-0.5 bg-background shadow-sm">
            Page de Garde • Support Pédagogique
          </Badge>
        </div>

        {/* Fiche autonome de la Page de Garde */}
        <div className="max-w-4xl w-full mx-auto my-auto animate-in fade-in zoom-in-95 duration-300">
          <div className="tut-page-de-garde border rounded-2xl overflow-hidden shadow-xl bg-card">
            {/* Image de couverture mise en valeur (Hero - grande taille claire) */}
            <div className="relative h-[340px] sm:h-[440px] w-full overflow-hidden bg-slate-950">
              {page.cover_image ? (
                <img
                  src={page.cover_image}
                  alt={page.title}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className={`w-full h-full ${theme.bgClass} flex items-center justify-center relative`}>
                  <div className="tut-card-banner-pattern" />
                  <div className="text-center text-white z-10 p-6">
                    <span className="text-7xl mb-3 block">{theme.icon}</span>
                    <span className="text-4xl font-black tracking-widest opacity-30">{theme.watermarkText}</span>
                  </div>
                </div>
              )}
              {/* Badge de catégorie overlay */}
              <div className="absolute top-3 left-3 z-10">
                <Badge className={`px-3 py-1 text-xs font-semibold shadow-md border ${theme.badgeClass}`}>
                  <span className="mr-1.5">{theme.icon}</span>
                  <span>{theme.label}</span>
                </Badge>
              </div>
            </div>

            {/* Corps de la Page de Garde compact */}
            <div className="p-4 sm:p-6">
              <div className="flex items-center gap-2.5 mb-2">
                {page.icon && <span className="text-xl sm:text-2xl flex-shrink-0 leading-none">{page.icon}</span>}
                <h1 className="text-base sm:text-lg font-bold text-foreground tracking-tight leading-snug">
                  {page.title}
                </h1>
              </div>

              {/* Description / Extrait compact */}
              {page.preview && (
                <div className="my-3 bg-muted/30 p-3 rounded-xl border border-border/40">
                  <p className="text-xs text-muted-foreground leading-relaxed italic">
                    {page.preview}
                  </p>
                </div>
              )}

              {/* Métadonnées compactes */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-4 pt-4 border-t border-border/60 text-xs">
                <div className="p-2.5 rounded-xl bg-muted/40 border flex flex-col justify-center">
                  <span className="text-muted-foreground uppercase text-[9px] font-bold tracking-wider flex items-center gap-1 mb-0.5">
                    <User size={11} className="text-indigo-500" /> Auteur
                  </span>
                  <span className="font-semibold text-foreground text-xs truncate">{page.author_name || 'Équipe pédagogique'}</span>
                </div>

                <div className="p-2.5 rounded-xl bg-muted/40 border flex flex-col justify-center">
                  <span className="text-muted-foreground uppercase text-[9px] font-bold tracking-wider flex items-center gap-1 mb-0.5">
                    <Layers size={11} className="text-indigo-500" /> Étapes
                  </span>
                  <span className="font-semibold text-foreground text-xs">
                    {headings.length || (page.children?.length ? page.children.length : 1)} étape(s)
                  </span>
                </div>

                <div className="p-2.5 rounded-xl bg-muted/40 border flex flex-col justify-center">
                  <span className="text-muted-foreground uppercase text-[9px] font-bold tracking-wider flex items-center gap-1 mb-0.5">
                    <BookOpen size={11} className="text-indigo-500" /> Temps
                  </span>
                  <span className="font-semibold text-foreground text-xs truncate">
                    {estimateReadingTime(page.word_count || 0)} ({page.word_count || 0} mots)
                  </span>
                </div>

                <div className="p-2.5 rounded-xl bg-muted/40 border flex flex-col justify-center">
                  <span className="text-muted-foreground uppercase text-[9px] font-bold tracking-wider flex items-center gap-1 mb-0.5">
                    <Clock size={11} className="text-indigo-500" /> Mis à jour
                  </span>
                  <span className="font-semibold text-foreground text-xs">
                    {page.updated_at ? formatDate(page.updated_at) : 'Récemment'}
                  </span>
                </div>
              </div>

              {/* Bouton Commencer la lecture */}
              <div className="mt-5 pt-4 border-t border-border/60 flex justify-center">
                <Button
                  onClick={() => setIsReadingMode(true)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-8 py-2.5 text-xs rounded-xl shadow-lg shadow-indigo-600/25 hover:scale-105 active:scale-95 transition-all gap-2 cursor-pointer"
                >
                  <BookOpen size={16} />
                  <span>Commencer la lecture</span>
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="text-center py-2 text-[11px] text-muted-foreground">
          EFIlearn • Espace Formateur & Administration Pédagogique
        </footer>
      </div>
    );
  }

  // Vue 2 : MODE LECTURE COMPLET (Sommaire + Article TipTap)
  return (
    <div className="tut-public-page">
      {/* Top Sticky Navbar */}
      <nav className="tut-public-navbar">
        <div className="tut-public-navbar-inner">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsReadingMode(false)}
              className="gap-1.5 text-xs font-semibold text-indigo-600 border-indigo-200 bg-indigo-50 hover:bg-indigo-100"
            >
              <ArrowLeft size={14} />
              Page de garde
            </Button>
            <span className="text-muted-foreground/30 text-xs">/</span>
            <span className="tut-public-navbar-title text-xs font-semibold text-foreground truncate max-w-[200px] sm:max-w-[360px]">
              {page.title}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              className="tut-public-navbar-toc-btn lg:hidden"
              onClick={() => setTocOpen(!tocOpen)}
            >
              <List size={15} />
              <span>Sommaire</span>
              {headings.length > 0 && <span className="tut-public-navbar-toc-badge">{headings.length}</span>}
            </button>
          </div>
        </div>
      </nav>

      {/* Body: TOC + Content */}
      <div className="tut-public-body">
        {/* Table of Contents - Sidebar */}
        <aside className="tut-public-toc">
          <div className="tut-public-toc-card">
            <h3 className="tut-public-toc-title flex items-center gap-1.5">
              <List size={14} />
              SOMMAIRE
            </h3>
            {headings.length > 0 ? (
              <ul className="tut-public-toc-list">
                {headings.map((h) => (
                  <li key={h.id} className="tut-public-toc-item">
                    <a
                      href={`#${h.id}`}
                      className={`tut-public-toc-link ${h.level === 2 ? 'tut-public-toc-link-h2' : ''} ${h.level === 3 ? 'tut-public-toc-link-h3' : ''} ${activeHeadingId === h.id ? 'active' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToHeading(h.id);
                      }}
                    >
                      {h.text}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="tut-public-toc-list">
                <li className="tut-public-toc-item">
                  <a
                    href="#tut-article-top"
                    className="tut-public-toc-link active"
                    onClick={(e) => {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    Consignes générales
                  </a>
                </li>
              </ul>
            )}
          </div>
        </aside>

        {/* Main Content Article */}
        <main className="tut-public-content">
          <article className="tut-public-article" id="tut-article-top" ref={articleRef}>
            {editor && <EditorContent editor={editor} />}
          </article>

          {/* Media Gallery */}
          {(media.images.length > 0 || media.videos.length > 0) && (
            <section className="tut-public-media-section">
              {/* Images */}
              {media.images.length > 0 && (
                <>
                  <h3 className="tut-public-media-title">
                    <ImageIcon size={18} className="text-indigo-500" />
                    Images ({media.images.length})
                  </h3>
                  <div className="tut-public-media-grid">
                    {media.images.map((img, idx) => (
                      <div
                        key={idx}
                        className="tut-public-media-thumb"
                        title={img.alt || img.title || `Image ${idx + 1}`}
                        // Ouvrir l'image seule contournerait la protection : désactivé
                        onClick={protectionActive ? undefined : () => window.open(img.src, '_blank')}
                        style={protectionActive ? { cursor: 'default' } : undefined}
                      >
                        <img
                          src={img.src}
                          alt={img.alt || `Image ${idx + 1}`}
                          loading="lazy"
                          draggable={false}
                          className={protectionActive ? 'tut-protected-img' : undefined}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Videos */}
              {media.videos.length > 0 && (
                <>
                  <h3 className="tut-public-media-title" style={{ marginTop: media.images.length > 0 ? '24px' : 0 }}>
                    <Film size={18} className="text-indigo-500" />
                    Vidéos ({media.videos.length})
                  </h3>
                  <div className="tut-public-video-list">
                    {media.videos.map((vid, idx) => (
                      <a
                        key={idx}
                        href={vid.src}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tut-public-video-item"
                      >
                        <Film size={16} className="text-indigo-500 flex-shrink-0" />
                        <span className="text-sm font-medium">{vid.title || `Vidéo ${idx + 1}`}</span>
                      </a>
                    ))}
                  </div>
                </>
              )}
            </section>
          )}

          {/* Navigation: Previous / Next */}
          {(page.prev_page || page.next_page) && (
            <nav className="tut-public-nav">
              {page.prev_page ? (
                <Link
                  to={`/t/${page.prev_page.id}`}
                  className="tut-public-nav-btn"
                >
                  <ChevronLeft size={20} className="text-indigo-500 flex-shrink-0" />
                  <div>
                    <div className="tut-public-nav-label">Précédent</div>
                    <div className="tut-public-nav-title">
                      {page.prev_page.icon || '📄'} {page.prev_page.title}
                    </div>
                  </div>
                </Link>
              ) : <div />}
              {page.next_page ? (
                <Link
                  to={`/t/${page.next_page.id}`}
                  className="tut-public-nav-btn"
                  style={{ justifyContent: 'flex-end', textAlign: 'right' }}
                >
                  <div>
                    <div className="tut-public-nav-label">Suivant</div>
                    <div className="tut-public-nav-title">
                      {page.next_page.icon || '📄'} {page.next_page.title}
                    </div>
                  </div>
                  <ChevronRight size={20} className="text-indigo-500 flex-shrink-0" />
                </Link>
              ) : <div />}
            </nav>
          )}
        </main>
      </div>

      {/* Protection du contenu (filigrane, masquage, journalisation) */}
      <ContentProtection
        containerRef={articleRef}
        pageId={pageId}
        viewer={viewer}
        enabled={protectionActive}
      />

      {/* Footer */}
      <footer className="tut-public-footer">
        Propulsé par EFIlearn — Plateforme de formation
      </footer>

      {/* Floating Quick Sommaire Button */}
      {headings.length > 0 && (
        <button
          className="tut-public-floating-toc-btn"
          onClick={() => setTocOpen(true)}
          title="Ouvrir le sommaire"
        >
          <List size={18} />
          <span>Sommaire</span>
        </button>
      )}

      {/* Actions flottantes : retour à la page principale + haut / bas de page.
          Présent sur toutes les tailles d'écran, à l'opposé du bouton Sommaire
          (bas-gauche) pour ne jamais se recouvrir. */}
      <div className="tut-public-fab-stack">
        {rootPage && (
          <button
            type="button"
            className="tut-public-fab tut-public-fab-home"
            onClick={() => navigate(`/t/${rootPage.id}`)}
            title={`Revenir à : ${rootPage.title}`}
            aria-label="Revenir à la page principale du tutoriel"
          >
            <Home size={17} />
            <span className="tut-public-fab-label">Page principale</span>
          </button>
        )}
        <button
          type="button"
          className="tut-public-fab tut-public-fab-icon"
          onClick={scrollToTop}
          title="Haut de page"
          aria-label="Aller en haut de la page"
        >
          <ArrowUp size={18} />
        </button>
        <button
          type="button"
          className="tut-public-fab tut-public-fab-icon"
          onClick={scrollToBottom}
          title="Bas de page"
          aria-label="Aller en bas de la page"
        >
          <ArrowDown size={18} />
        </button>
      </div>

      {/* Floating Sommaire Drawer / Modal */}
      {headings.length > 0 && tocOpen && (
        <div className="tut-public-toc-drawer-overlay" onClick={() => setTocOpen(false)}>
          <div className="tut-public-toc-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4 border-b pb-2">
              <h3 className="tut-public-toc-title text-sm font-bold flex items-center gap-2 mb-0">
                <List size={16} />
                Sommaire
              </h3>
              <button
                className="text-muted-foreground hover:text-foreground p-1 rounded-md"
                onClick={() => setTocOpen(false)}
              >
                <X size={18} />
              </button>
            </div>
            <ul className="tut-public-toc-list max-h-[60vh] overflow-y-auto pr-2">
              {headings.map((h) => (
                <li key={h.id} className="tut-public-toc-item">
                  <a
                    href={`#${h.id}`}
                    className={`tut-public-toc-link ${h.level === 2 ? 'tut-public-toc-link-h2' : ''} ${h.level === 3 ? 'tut-public-toc-link-h3' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setTocOpen(false);
                      scrollToHeading(h.id);
                    }}
                  >
                    {h.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
